import { useQuery, type QueryClient } from "@tanstack/react-query";
import { parseAbiItem, parseEventLogs, type Address, type Log } from "viem";
import type { RedKeel } from "../config/networks";
import { clienteLogs, logsHistoricos } from "../lib/cliente";

/**
 * TODO lo que ha emitido un vault, en UNA consulta.
 *
 * EL FALLO QUE ARREGLA (2026-08-19): en BNB Chain el historial salía vacío. Las tres pantallas que
 * leen eventos —el historial, la actividad del robot y el desglose del gas— pedían un `eth_getLogs`
 * POR EVENTO y desde el bloque 0. BNB Chain no acepta ese rango (ver `networks.ts`), así que las 18
 * consultas del historial fallaban las 18, el `catch` devolvía lista vacía, y la pestaña se quedaba
 * en blanco como si el vault no hubiera hecho nada nunca.
 *
 * Aquí se pide SIN filtrar por evento —todo lo que emite esa dirección— y se decodifica en local
 * con `parseEventLogs`. Dos ventajas sobre filtrar en el RPC:
 *
 *   · TROCEA CUANDO HACE FALTA. `logsHistoricos` recorre la cadena por ventanas donde el proveedor
 *     no la acepta entera, y devuelve `null` —no una lista corta— si no consigue recorrerla toda.
 *   · UNA CONSULTA EN VEZ DE DIECIOCHO. Antes cada evento era una ida y vuelta, y con troceo serían
 *     dieciocho por ventana. Filtrar en local no cuesta nada: los eventos de un vault son cientos,
 *     no millones.
 *
 * Las tres pantallas comparten esta query (misma clave), así que abrir la ficha pide los eventos
 * una vez y no tres.
 */
export const EVENTOS_VAULT = [
  parseAbiItem("event Opened(uint256 costBasisRef, int24 openTick)"),
  parseAbiItem("event Rebalanced(uint256 indexed oldTokenId, uint256 indexed newTokenId, int24 tickLower, int24 tickUpper)"),
  parseAbiItem("event Recovered(int24 tickLower, int24 tickUpper, uint256 target, uint256 sold, bool capped)"),
  parseAbiItem("event Reinjected(uint256 amount, uint256 reserveLeft, uint8 mode)"),
  parseAbiItem("event FeesCollected(uint256 net0, uint256 net1, bool held)"),
  parseAbiItem("event PerfFeeTaken(address indexed treasury, uint256 fee0, uint256 fee1)"),
  parseAbiItem("event Deposited(address indexed token, uint256 amount)"),
  parseAbiItem("event Withdrawn(address indexed to, uint256 amount0, uint256 amount1)"),
  parseAbiItem("event FeesWithdrawn(uint256 profit0, uint256 profit1)"),
  parseAbiItem("event ProfitReinvested(uint256 profit0, uint256 profit1)"),
  parseAbiItem("event Swept(uint256 amount0, uint256 amount1)"),
  parseAbiItem("event GasReimbursed(address indexed keeper, uint256 amount0, uint256 amount1)"),
  parseAbiItem("event ReserveAdded(uint256 amount, uint256 reserve, uint256 reserveInitial)"),
  parseAbiItem("event RebalancerUpgraded(address indexed anterior, address indexed nuevo)"),
  parseAbiItem("event GasBudgetLow(uint256 remaining0, uint256 remaining1)"),
  parseAbiItem("event GasShortfall(address indexed keeper, uint256 want0, uint256 paid0, uint256 want1, uint256 paid1)"),
  parseAbiItem("event PausedSet(bool paused)"),
  parseAbiItem("event ConfigUpdated()"),
] as const;

export type EventoVault = Log<bigint, number, false> & {
  eventName: string;
  args: Record<string, unknown>;
};

const CLAVE = (red: RedKeel | undefined, vault: Address | null) => ["eventos", red?.clave, vault];

async function leer(red: RedKeel, vault: Address): Promise<EventoVault[]> {
  const crudos = await logsHistoricos<Log<bigint, number, false>>(
    clienteLogs(red),
    { address: vault },
    red
  );
  // NO se devuelve lista vacía cuando no se pudo leer: «este vault no ha hecho nada» y «no he
  // podido preguntarlo» son cosas distintas, y confundirlas es lo que dejaba la pestaña en blanco
  // sin decir por qué. Con el error, la pantalla lo cuenta.
  if (!crudos) throw new Error("Esta red no deja leer el historial entero ahora mismo.");

  return parseEventLogs({ abi: EVENTOS_VAULT, logs: crudos }) as unknown as EventoVault[];
}

export function useEventos(vault: Address | null, red: RedKeel | undefined) {
  return useQuery({
    queryKey: CLAVE(red, vault),
    enabled: !!vault && !!red,
    staleTime: 60_000,
    queryFn: () => leer(red!, vault!),
  });
}

/**
 * Lo mismo, para hooks que ya tienen su propia consulta y solo quieren los eventos dentro.
 * `fetchQuery` con la MISMA clave que el hook: la ficha del vault abre tres pantallas que los
 * necesitan y así se piden una vez, no tres.
 */
export const eventosDeVault = (qc: QueryClient, red: RedKeel, vault: Address) =>
  qc.fetchQuery({ queryKey: CLAVE(red, vault), staleTime: 60_000, queryFn: () => leer(red, vault) });

/** Los de un tipo, ya decodificados. Sustituye a la consulta por evento que había en cada hook. */
export const soloDe = (logs: EventoVault[] | undefined, evento: string) =>
  (logs ?? []).filter((l) => l.eventName === evento);
